import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchShipmentList,
  setListPage,
  setListStatusFilter,
  resetDraftShipment,
} from '../../store/slices/shipmentTrackerSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import { ShipmentTrackerService } from '../../api/generated/services/ShipmentTrackerService';
import ShipmentStatusBadge from './ShipmentStatusBadge';
import ShipmentStatusFilter from './ShipmentStatusFilter';
import Pagination from '../Pagination';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';

interface ActiveShipmentsTableProps {
  showViewAllLink?: boolean;
  showCsvButton?: boolean;
  /** Passed from Dashboard to limit displayed rows */
  limitOverride?: number;
}

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$' };

function formatQtyCartons(units: number, cartons: number, t: (k: string, o?: Record<string, unknown>) => string) {
  const parts: string[] = [];
  if (units > 0) parts.push(t('shipment_qty_units', { count: units }));
  if (cartons > 0) parts.push(t('shipment_qty_cartons', { count: cartons }));
  return parts.join(' · ') || '-';
}

function formatOriginDest(item: ShipmentListItemView) {
  const origin = [item.originCity, item.originCountryCode].filter(Boolean).join(', ');
  const dest = [item.destinationCity, item.destinationCountryCode].filter(Boolean).join(', ');
  if (!origin && !dest) return '-';
  if (!origin) return `→ ${dest}`;
  if (!dest) return origin;
  return `${origin} → ${dest}`;
}

const ActiveShipmentsTable: React.FC<ActiveShipmentsTableProps> = ({
  showViewAllLink = false,
  showCsvButton = false,
  limitOverride,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canWrite, canViewFinancials } = useShipmentTrackerPermissions();

  const { items, total, page, limit, statusFilter, loading } = useAppSelector(
    (state) => state.shipmentTracker.list
  );

  const [search, setSearch] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);

  const handleCsvExport = useCallback(async () => {
    if (csvLoading) return;
    setCsvLoading(true);
    try {
      // The generated client parses text/csv responses as a string, so wrap it
      // in a Blob before creating the object URL.
      const csv = await ShipmentTrackerService.getAppShipmentTrackerExport();
      const blob = new Blob([csv as string], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shipments.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setCsvLoading(false);
    }
  }, [csvLoading]);

  const effectiveLimit = limitOverride ?? limit;

  const load = useCallback(() => {
    dispatch(
      fetchShipmentList({
        page,
        limit: effectiveLimit,
        status: statusFilter.length > 0 ? statusFilter : undefined,
      })
    );
  }, [dispatch, page, effectiveLimit, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageChange = (direction: 'prev' | 'next') => {
    dispatch(setListPage(direction === 'prev' ? page - 1 : page + 1));
  };

  const handleStatusChange = (statuses: string[]) => {
    dispatch(setListStatusFilter(statuses));
  };

  // Client-side search filter
  const displayed = search.trim()
    ? items.filter((item) => {
        const q = search.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          (item.reference ?? '').toLowerCase().includes(q)
        );
      })
    : items;

  const totalPages = Math.max(1, Math.ceil(total / effectiveLimit));
  const startItem = total === 0 ? 0 : (page - 1) * effectiveLimit + 1;
  const endItem = Math.min(page * effectiveLimit, total);

  const handleRowClick = (id: string) => {
    navigate(`/features/shipment-tracker/shipments/${id}`);
  };

  return (
    <div className="shipments-table-container">
      <div className="shipments-table-toolbar">
        <input
          className="input"
          type="search"
          placeholder={t('shipment_search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ShipmentStatusFilter
          selected={statusFilter}
          onChange={handleStatusChange}
        />
        <div className="shipments-table-toolbar__actions">
          {showCsvButton && (
            <button
              className="btn btn-secondary"
              onClick={handleCsvExport}
              disabled={csvLoading}
            >
              {t('shipment_export_csv')}
            </button>
          )}
          {canWrite && (
            <button
              className="btn btn-primary"
              onClick={() => {
                dispatch(resetDraftShipment());
                navigate('/features/shipment-tracker/shipments/new');
              }}
            >
              {t('shipment_new_button')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="shipments-table-loading">{t('loading')}</div>
      ) : displayed.length === 0 ? (
        <div className="shipments-table-empty">{t('shipment_list_empty')}</div>
      ) : (
        <div className="shipments-table-scroll">
        <table className="shipments-table">
          <thead>
            <tr>
              <th>{t('shipment_col_name_ref')}</th>
              <th>{t('shipment_col_origin_dest')}</th>
              <th>{t('shipment_col_status')}</th>
              <th>{t('shipment_col_created')}</th>
              <th>{t('shipment_col_eta')}</th>
              <th>{t('shipment_col_qty_cartons')}</th>
              {canViewFinancials && <th>{t('shipment_col_total')}</th>}
            </tr>
          </thead>
          <tbody>
            {displayed.map((item) => {
              const isDraft = item.workflowStatus === 'draft';
              const currencySymbol = CURRENCY_SYMBOL[item.currency] ?? item.currency;
              return (
                <tr
                  key={item.id}
                  className="shipments-table__row"
                  onClick={() => handleRowClick(item.id)}
                >
                  <td className="shipments-table__name-ref">
                    <span className="shipments-table__name">{item.name}</span>
                    {item.reference && (
                      <span className="shipments-table__ref">{item.reference}</span>
                    )}
                    {isDraft && (
                      <ShipmentStatusBadge status={null} isDraft />
                    )}
                  </td>
                  <td>{formatOriginDest(item)}</td>
                  <td>
                    {!isDraft && (
                      <ShipmentStatusBadge status={item.executionStatus} />
                    )}
                  </td>
                  <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                  <td>{item.eta ?? '-'}</td>
                  <td>
                    {formatQtyCartons(item.totalUnits ?? 0, item.totalCartons ?? 0, t)}
                  </td>
                  {canViewFinancials && (
                    <td>
                      {item.total != null ? `${currencySymbol}${item.total}` : '-'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      <div className="shipments-table-footer">
        {showViewAllLink && (
          <button
            className="btn btn-link"
            onClick={() => navigate('/features/shipment-tracker/shipments')}
          >
            {t('shipment_view_all')}
          </button>
        )}
        <Pagination
          startItem={startItem}
          endItem={endItem}
          total={total}
          totalPages={totalPages}
          page={page}
          loading={loading}
          handlePageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default ActiveShipmentsTable;
